/**
 * VP-213 — Net Profit / Net ROAS per wiersz tabeli kampanii (tryb e-commerce).
 *
 * Liczone z revenue INTEGRACJI (tracking, netto po zwrotach — VP-212), nigdy
 * z Meta-pixel purchaseValue. Koszty ZMIENNE wg stawek z serwera
 * (GET /api/ecommerce-costs/rates): globalne + przypięte do kampanii.
 * Koszty stałe (monthly/one_time) celowo NIE są alokowane na kampanie —
 * zostają w karcie globalnej (ProfitCard). Koszty produktowe też nie
 * (brak atrybucji line-itemów do kampanii — VP-214).
 */

import type { VariableRatesSummary } from "@shared/profit-rates";

export type { VariableRates, VariableRatesSummary } from "@shared/profit-rates";

export type RowProfit = {
  netProfit: number;
  /** POAS = netProfit / spend. Null gdy spend = 0. */
  netRoas: number | null;
  variableCosts: number;
};

/**
 * Net Profit wiersza = revenue − spend − koszty zmienne.
 * Zwraca null TYLKO gdy brak danych (nieprzypisane revenue z integracji /
 * stawki jeszcze się ładują) — wtedy '—'. Revenue 0 lub UJEMNE (zwroty netto,
 * VP-212) jest realną informacją i liczy stratę; ukrywanie go chowało
 * najgorsze kampanie za '—' (review VP-213).
 */
export function computeRowProfit(opts: {
  revenue: number | undefined;
  spend: number;
  purchases: number | undefined;
  campaignId: string;
  rates: VariableRatesSummary | undefined;
}): RowProfit | null {
  const { revenue, spend, purchases, campaignId, rates } = opts;
  if (revenue === undefined || !Number.isFinite(revenue) || !rates) return null;

  const g = rates.global;
  const c = rates.byCampaign[campaignId];
  const pct =
    g.cogsPct +
    g.shippingPct +
    g.platformFeePct +
    (c ? c.cogsPct + c.shippingPct + c.platformFeePct : 0);
  const fixedPerOrder =
    g.cogsFixedPerOrder +
    g.shippingFixedPerOrder +
    (c ? c.cogsFixedPerOrder + c.shippingFixedPerOrder : 0);

  // Przy revenue ujemnym (zwroty netto > zakupy w oknie) koszty zmienne
  // liczą się od zera — "ujemny COGS" nie istnieje.
  const pctBase = Math.max(0, revenue);
  const variableCosts = pctBase * pct + (purchases ?? 0) * fixedPerOrder;
  const netProfit = revenue - spend - variableCosts;
  return {
    netProfit,
    netRoas: spend > 0 ? netProfit / spend : null,
    variableCosts,
  };
}
